import { useCallback, useMemo, useState, memo } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { StepsModal } from 'cohort-creation-constructor';
import { toast } from 'react-toastify';

import { deleteReport, sendRequest } from '@/api';
import { ListItem, ListViewModal, LoadingScreen, Modal, Pagination, Select, ToastNotification } from '@/components';
import { API_BASE_URLS, DEFAULT_PAGE_SIZE, QUERY_KEYS, REPORT_LIST_TYPE_OPTIONS } from '@/constants';
import { getReportQuery } from '@/query';
import { ListItemAction, ListItemType, ReportSaveMode, SelectOption } from '@/types';

import styles from './styles.module.scss';

const defaultStepModalState = {
  isOpen: false,
  editMode: false,
};

export const ProspectingList = memo(() => {
  const [stepModal, setStepModal] = useState(defaultStepModalState);
  const [activeItem, setActiveItem] = useState<ListItemType | null>(null);
  const [page, setPage] = useState(0);
  const [selectedMode, setSelectedMode] = useState<SelectOption<ReportSaveMode> | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const {
    data,
    isLoading,
    refetch: refetchList,
  } = useQuery(getReportQuery(page * DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE, selectedMode ? selectedMode.value : null));

  const { mutate: deleteReportList } = useMutation({
    mutationFn: async () => {
      if (!activeItem) return;
      setDeleteModalOpen(false);
      await deleteReport(activeItem.id);
      await refetchList();
    },
    onSuccess: () => {
      toast(<ToastNotification type="success" title="Report deleted successfully" />);
      setActiveItem(null);
    },
    onError: (error) => {
      toast(<ToastNotification type="error" title="Error" message={error.message} />);
      setActiveItem(null);
    },
  });

  const { mutate: sendSubmitRequest, isPending: isSubmitting } = useMutation({
    mutationKey: [QUERY_KEYS.SEND_REQUEST],
    mutationFn: async (reportId: number) => {
      await sendRequest(reportId);
      await refetchList();
    },
    onSuccess: () => {
      toast(<ToastNotification type="success" title="Report successfully sent" />);
      setActiveItem(null);
    },
    onError: (error) => {
      toast(<ToastNotification type="error" title="Error" message={error.message} />);
      setActiveItem(null);
    },
  });

  const reportResponse = data?.data;
  const listData = useMemo(() => reportResponse?.items || [], [reportResponse]);

  const selectedItem = useMemo(() => {
    return listData.find((item) => item.id === activeItem?.id);
  }, [listData, activeItem]);

  const handleModeChange = useCallback((mode: SelectOption<ReportSaveMode> | null) => {
    setSelectedMode(mode);
    setPage(0);
  }, []);

  // Delete modal
  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
    setActiveItem(null);
  }, []);

  const handleDeleteItem = useCallback(() => deleteReportList(), [deleteReportList]);

  // View modal
  const handleViewModalClose = useCallback(() => {
    setViewModalOpen(false);
    setActiveItem(null);
  }, []);

  // Cohort constructor modal
  const handleCohortModalOpen = useCallback(() => {
    setStepModal({ isOpen: true, editMode: false });
  }, []);

  const handleCohortModalClose = useCallback(() => {
    setStepModal(defaultStepModalState);
    setActiveItem(null);
  }, []);

  const handleCohortSuccess = useCallback(async () => {
    toast(
      <ToastNotification type="success" title={`List was successfully ${stepModal.editMode ? 'updated' : 'saved'}`} />,
    );
    setStepModal(defaultStepModalState);
    setActiveItem(null);
    await refetchList();
  }, [refetchList, stepModal.editMode]);

  const handleCohortError = useCallback(() => {
    setStepModal(defaultStepModalState);
    toast(<ToastNotification type="error" title={`Failed to ${stepModal.editMode ? 'update' : 'save'} list`} />);
  }, [stepModal.editMode]);

  // List item actions handler
  const handleProceedItemAction = useCallback(
    (action: ListItemAction, item: ListItemType) => {
      setActiveItem(item);

      switch (action) {
        case ListItemAction.delete:
          setDeleteModalOpen(true);
          break;

        case ListItemAction.edit:
          setStepModal({
            isOpen: true,
            editMode: true,
          });
          break;

        case ListItemAction.view:
          setViewModalOpen(true);
          break;

        case ListItemAction.submit:
          sendSubmitRequest(item.id);
          break;
      }
    },
    [sendSubmitRequest],
  );

  return (
    <div className={styles.container}>
      <div className={styles.actionsContainer}>
        <button type="button" className={styles.createNewListBtn} onClick={handleCohortModalOpen}>
          + Create New List
        </button>

        <Select<ReportSaveMode>
          label="List Type:"
          options={REPORT_LIST_TYPE_OPTIONS}
          value={selectedMode}
          onChange={handleModeChange}
        />
      </div>

      <div className={styles.listContainer}>
        {isLoading ? (
          <LoadingScreen />
        ) : listData.length ? (
          listData.map((item: ListItemType) => (
            <ListItem
              key={item.id}
              {...item}
              isSubmitting={item.id === activeItem?.id ? isSubmitting : false}
              onItemClick={handleProceedItemAction}
            />
          ))
        ) : (
          <p>No results</p>
        )}
      </div>

      <Pagination
        totalItems={reportResponse?.total || 0}
        pageSize={DEFAULT_PAGE_SIZE}
        currentPage={page}
        setCurrentPage={setPage}
        disabled={isLoading}
      />

      <StepsModal
        apiBaseUrl={API_BASE_URLS.BASE_URL}
        isOpen={stepModal.isOpen}
        handleCohortModalClose={handleCohortModalClose}
        onSuccess={handleCohortSuccess}
        onError={handleCohortError}
        configurationId={selectedItem?.id}
        title={`${activeItem ? 'Update' : 'Create'} comparison cohort`}
      />

      <Modal open={deleteModalOpen} onClose={handleDeleteModalClose} title="Delete list">
        <p className={styles.modalText}>
          Are you sure you want to delete <b>{selectedItem?.name}</b>?
        </p>

        <div className={styles.modalActionsContainer}>
          <button className={clsx(styles.button, styles.cancelBtn)} onClick={handleDeleteModalClose}>
            Cancel
          </button>

          <button className={clsx(styles.button, styles.submitBtn)} onClick={handleDeleteItem}>
            Delete
          </button>
        </div>
      </Modal>

      <ListViewModal
        open={viewModalOpen && !!activeItem}
        selectedListItem={activeItem}
        handleClose={handleViewModalClose}
      />
    </div>
  );
});

ProspectingList.displayName = 'ProspectingList';
